import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '../context/SessionContext.jsx';
import { signOut } from '../api/client.js';

const TITLES = {
  '/': 'Home',
  '/assistant': 'Vizora AI Data Assistant',
  '/admin/users': 'User Setup',
  '/admin/profiles': 'Profile Management',
  '/admin/catalogue': 'Power BI Content',
  '/admin/paginated': 'Pagination Setup',
  '/admin/audit': 'Audit Log',
  '/admin/usage': 'Report Usage',
  '/platform/customers': 'Customers · Vizora staff'
};

export default function Topbar({ onOpenMenu }) {
  const { user, theme, toggleTheme } = useSession();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const isDark = theme === 'dark';

  /* Empty the cache before leaving for the Entra logout, not after.
     Whatever is in memory at the moment of navigation is what the browser
     snapshots into the back/forward cache, so clearing first means a visitor
     who presses back restores an empty cache and refetches - rather than
     restoring this user's menu, name and data into a browser that has since
     signed somebody else in.

     SessionProvider also clears on a bfcache restore. Two guards on purpose:
     this one does not depend on the pageshow event firing, and that one
     catches restores that did not begin with a sign-out. */
  const leave = async () => {
    queryClient.clear();
    await signOut();
  };

  // Page title only. It used to fall back to the customer name, which meant
  // the seeded "Development" tenant showed up in the chrome on every page
  // that wasn't in this map.
  const title = TITLES[pathname]
    ?? (pathname.startsWith('/report/') || pathname.startsWith('/paginated/') ? 'Report' : null)
    ?? (pathname.startsWith('/dashboard/') ? 'Dashboard' : null)
    ?? '';

  return (
    <header className="portal-topbar">
      <div className="d-flex align-items-center gap-2 min-w-0">
        <button type="button" className="btn btn-sm btn-link topbar-menu-btn"
                onClick={onOpenMenu} aria-label="Open menu">
          <i className="bi bi-list fs-5" />
        </button>
        <span className="topbar-title text-truncate">{title}</span>
      </div>

      <div className="d-flex align-items-center gap-2">
        <button type="button" className="btn btn-sm btn-link" onClick={toggleTheme}
                title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}>
          <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`} />
        </button>

        <div className="d-none d-sm-block text-end topbar-user">
          <div className="name text-truncate" style={{ maxWidth: 200 }}>
            {user?.fullName ?? user?.email}
          </div>
          {user?.jobRole && <div className="role text-truncate">{user.jobRole}</div>}
        </div>

        <div className="topbar-avatar" aria-hidden="true">{initials(user)}</div>

        <button type="button" className="btn btn-sm btn-outline-secondary ms-1"
                onClick={leave}>
          <i className="bi bi-box-arrow-right d-sm-none" />
          <span className="d-none d-sm-inline">Sign out</span>
        </button>
      </div>
    </header>
  );
}

function initials(user) {
  const source = user?.fullName?.trim() || user?.email || '';
  if (!source) return '?';
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || source[0].toUpperCase();
}
